'use client';

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  V3Layout, V3Reveal, V3RevealSection, KineticTitle, ImageGalaxy, Pill,
  Eyebrow, useLang, formatDate, venueShort,
  ACCENT, INK, LINE, DARK, PAPER, type GalaxyItem,
} from './_layout';
import { appLanguageToContentLocale } from '@/i18n/contentLocale';
// Content single-sourced from the v2 home page (trilingual copy + data).
import { COPY, FEAT_TXT, toMs, type Activity } from '@/app/v2/page';
import { PARTNERS } from '@/app/v2/page';

/**
 * Pathin — v3 home page. wolverineworldwide.com-style: stark black & white,
 * heavy grotesk, Lenis smooth scroll, scattered image galaxy, pill buttons,
 * sparse huge-type sections. Mounted at /next; leaves live v2 untouched.
 */

/* The galaxy of scattered concert/poster thumbnails (real PathIN images).
   x/y are % of the container; size in px; speed = parallax travel. */
const GALAXY: GalaxyItem[] = [
  // top half (slides up) — speed = magnitude of the slide
  { src: '/images/events/summer-fades-away/recap-05.jpg',     x: '1%',  y: '6%',  size: 92,  speed: 200, rotate: -3 },
  { src: '/images/events/oys-berlin-2026/photo-JM502226.jpg', x: '24%', y: '2%',  size: 104, speed: 280, rotate: -2 },
  { src: '/images/events/summer-fades-away/cover.webp',       x: '55%', y: '4%',  size: 116, speed: 150, rotate: 2 },
  { src: '/images/events/oys-berlin-2026/poster-berlin.webp', x: '88%', y: '3%',  size: 70,  speed: 300, rotate: -3 },
  { src: '/images/events/oys-berlin-2026/photo-JM501882.jpg', x: '13%', y: '22%', size: 128, speed: 130, rotate: 2 },
  { src: '/images/events/summer-fades-away/recap-06.jpg',     x: '78%', y: '18%', size: 86,  speed: 250, rotate: 3 },
  { src: '/images/events/summer-fades-away/recap-04.jpg',     x: '40%', y: '30%', size: 72,  speed: 180, rotate: -2 },
  { src: '/images/highlights/fazi.jpg',                       x: '5%',  y: '40%', size: 78,  speed: 230, rotate: 2 },
  { src: '/images/events/summer-fades-away/live-cologne.webp',x: '90%', y: '40%', size: 96,  speed: 160, rotate: -2 },
  // bottom half (slides down)
  { src: '/images/events/summer-fades-away/live-stage.webp',  x: '8%',  y: '58%', size: 110, speed: 260, rotate: -2 },
  { src: '/images/events/oys-berlin-2026/photo-JM502484.jpg', x: '64%', y: '54%', size: 140, speed: 150, rotate: -2 },
  { src: '/images/highlights/an-corporation.jpg',             x: '86%', y: '60%', size: 100, speed: 220, rotate: -2 },
  { src: '/images/events/summer-fades-away/recap-10.jpg',     x: '46%', y: '72%', size: 90,  speed: 300, rotate: -3 },
  { src: '/images/events/summer-fades-away/recap-09.jpg',     x: '22%', y: '76%', size: 84,  speed: 200, rotate: 3 },
  { src: '/images/events/summer-fades-away/live-wide.webp',   x: '74%', y: '78%', size: 116, speed: 170, rotate: 2 },
  { src: '/images/events/summer-fades-away/recap-11.jpg',     x: '38%', y: '90%', size: 70,  speed: 250, rotate: 2 },
  { src: '/images/events/summer-fades-away/recap-12.jpg',     x: '4%',  y: '86%', size: 80,  speed: 150, rotate: -2 },
  { src: '/images/events/summer-fades-away/live-drums.webp',  x: '90%', y: '88%', size: 88,  speed: 280, rotate: -3 },
];

/* Split a title into staggered lines (wolverine's descending-stagger headline).
   Latin: split words into two lines; CJK: split in half by character. The
   trailing period stays on the last line so its accent dot still renders. */
function staggerText(s: string): string {
  if (s.includes(' ')) {
    const w = s.split(' ');
    const mid = Math.ceil(w.length / 2);
    return w.slice(0, mid).join(' ') + '\n' + w.slice(mid).join(' ');
  }
  const tail = s.match(/[。.?!？！]+$/)?.[0] ?? '';
  const core = tail ? s.slice(0, -tail.length) : s;
  if (core.length >= 4) {
    const h = Math.ceil(core.length / 2);
    return core.slice(0, h) + '\n' + core.slice(h) + tail;
  }
  return s;
}

export default function V3Home() {
  const lang = useLang();
  const t = COPY[lang];
  const ftx = FEAT_TXT[lang];
  const disp = lang === 'CN' ? 'v3-display-cjk' : 'v3-display';
  const [events, setEvents] = useState<Activity[]>([]);

  useEffect(() => {
    const apiLang = appLanguageToContentLocale(lang);
    fetch(`/api/activities?language=${apiLang}&pageSize=50`)
      .then((r) => r.json())
      .then((data) => setEvents(Array.isArray(data) ? data : data?.activities ?? data?.data ?? []))
      .catch(() => setEvents([]));
  }, [lang]);

  const nowMs = Date.now();
  const sorted = [...events]
    .filter((e) => e.slots && e.slots.length > 0)
    .sort((a, b) => new Date(b.slots[0].start_time.replace(' ', 'T')).getTime() - new Date(a.slots[0].start_time.replace(' ', 'T')).getTime());

  const featured = [...events].filter((e) => e.featured_rank != null).sort((a, b) => a.featured_rank! - b.featured_rank!)[0];
  const featuredState: 'active' | 'recap' | null = !featured ? null
    : Math.max(...featured.slots.map((s) => toMs(s.end_time))) >= nowMs ? 'active' : 'recap';

  const hlRecency = (e: Activity) => toMs(e.display_date ?? e.slots[0]?.start_time);
  const hlPinned = events.filter((e) => e.highlight_rank != null).sort((a, b) => a.highlight_rank! - b.highlight_rank!);
  const hlAuto = events.filter((e) => e.show_in_highlights && e.highlight_rank == null).sort((a, b) => hlRecency(b) - hlRecency(a));
  const highlights = [...hlPinned, ...hlAuto].slice(0, 8);

  return (
    <V3Layout>
      <Hero t={t} disp={disp} latest={sorted[0] ?? featured} ftx={ftx} lang={lang} />

      {/* The hero closes itself with its own rounded bottom edge; the rest of
          the page flows flush below it (no overlapping lip). */}
      <div className="relative z-10 v3-canvas">

      {/* INTRO — asymmetric: empty left, right-aligned statement + pill */}
      <V3RevealSection className="px-6 md:px-10 pt-28 md:pt-40 pb-20">
        <div className="mx-auto max-w-[1480px] grid md:grid-cols-12 gap-8">
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-[clamp(17px,1.9vw,27px)] leading-[1.4] tracking-[-0.015em] font-medium v3-ink">{t.hero.lede}</p>
            <div className="mt-10"><Pill to="/next/about">{lang === 'CN' ? '了解 PathIN' : lang === 'DE' ? 'Mehr über uns' : 'Learn more about us'}</Pill></div>
          </div>
        </div>
      </V3RevealSection>

      {/* GALAXY — the signature scattered-image band */}
      <section className="relative px-6 md:px-10 overflow-hidden">
        <div className="mx-auto max-w-[1480px] relative h-[92vh] min-h-[640px] flex flex-col items-center justify-center">
          <div className="absolute inset-0 hidden md:block pointer-events-none">
            <ImageGalaxy items={GALAXY} className="h-full w-full" />
          </div>
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-fit text-left">
              <KineticTitle stagger text={staggerText(t.highlights.title)} className={`${disp} text-[clamp(38px,7vw,112px)]`} />
            </div>
            <p className="mt-8 text-center text-[clamp(15px,1.2vw,18px)] v3-soft max-w-[40ch]">{t.highlights.lede}</p>
            <div className="mt-10"><Pill to="/next/events">{t.archive.ctaInline}</Pill></div>
          </div>
        </div>
        {/* mobile fallback strip */}
        <div className="md:hidden grid grid-cols-3 gap-2 pb-16">
          {GALAXY.slice(0, 6).map((g) => (
            <div key={g.src} className="aspect-square overflow-hidden rounded"><img src={g.src} alt="" className="w-full h-full object-cover" /></div>
          ))}
        </div>
      </section>

      {/* FEATURED — split dark card (image | black panel) */}
      {featured && (
        <V3RevealSection className="px-6 md:px-10 py-12 md:py-20">
          <div className="mx-auto max-w-[1480px]">
            <Link to={`/next/events/${featured.id}`} className="group grid md:grid-cols-2 rounded-2xl overflow-hidden" style={{ backgroundColor: DARK }}>
              <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden">
                {featured.cover_url && <img src={featured.cover_url} alt={featured.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />}
              </div>
              <div className="p-8 md:p-14 flex flex-col justify-between text-white min-h-[320px]">
                <span className="text-[12px] tracking-[0.16em] uppercase font-semibold" style={{ color: ACCENT }}>{featuredState === 'active' ? ftx.active : ftx.recap}</span>
                <div className="mt-10">
                  <h2 className="v3-display-cjk text-[clamp(26px,3.4vw,48px)]">{featured.title}<span style={{ color: ACCENT }}>.</span></h2>
                  {featured.subtitle && <p className="mt-3 text-white/60 text-[clamp(16px,1.4vw,22px)]">{featured.subtitle}</p>}
                  <div className="mt-8"><span className="v3-pill v3-pill--dark">{featuredState === 'active' ? ftx.ctaActive : ftx.ctaRecap}<span className="v3-pill__arrow" aria-hidden>→</span></span></div>
                </div>
              </div>
            </Link>
          </div>
        </V3RevealSection>
      )}

      {/* PROGRAMS — statement + two big blocks */}
      <V3RevealSection className="px-6 md:px-10 py-24 md:py-36">
        <div className="mx-auto max-w-[1480px]">
          <KineticTitle text={t.programs.title} className={`${disp} text-[clamp(32px,5vw,88px)] max-w-[16ch]`} />
          <p className="mt-7 text-[clamp(15px,1.2vw,17px)] v3-soft max-w-[52ch]">{t.programs.lede}</p>
          <div className="mt-16 grid md:grid-cols-2 gap-px" style={{ backgroundColor: LINE }}>
            <ProgramBlock to="/GraviLens" kicker={t.programs.gravilens.kicker} title="GraviLens" sub="引力透镜" body={t.programs.gravilens.body} cta={t.programs.gravilens.cta} />
            <ProgramBlock to="/Qiaodong" kicker={t.programs.qiaodong.kicker} title="Qiaodong" sub="桥洞" body={t.programs.qiaodong.body} cta={t.programs.qiaodong.cta} />
          </div>
        </div>
      </V3RevealSection>

      {/* BY THE NUMBERS — oversized figures */}
      <section className="px-6 md:px-10 py-24 md:py-32" style={{ borderTop: `1px solid ${LINE}` }}>
        <div className="mx-auto max-w-[1480px] grid md:grid-cols-3 gap-12 md:gap-8">
          <BigStat value="5" label={t.hero.stats.tours} />
          <BigStat value="11+" label={t.hero.stats.cities} />
          <BigStat value={t.hero.foundedValue} label={t.hero.stats.founded} small />
        </div>
      </section>

      {/* LATEST — horizontal scroll carousel of recent events */}
      {sorted.length > 0 && (
        <V3RevealSection className="py-24 md:py-32" style={{ borderTop: `1px solid ${LINE}` }}>
          <div className="mx-auto max-w-[1480px] px-6 md:px-10 flex items-end justify-between mb-10">
            <h3 className={`${disp} text-[clamp(26px,3.2vw,48px)]`}>{t.archive.title}<span style={{ color: ACCENT }}>.</span></h3>
            <Link to="/next/events" className="hidden md:inline text-[14px] font-medium v3-soft hover:text-[#050505]">{t.archive.ctaInline} →</Link>
          </div>
          <div className="flex gap-5 overflow-x-auto px-6 md:px-10 pb-6 snap-x" style={{ scrollbarWidth: 'none' }}>
            {sorted.slice(0, 10).map((ev) => {
              const f = formatDate(ev.slots[0].start_time, lang);
              return (
                <Link key={ev.id} to={`/next/events/${ev.id}`} className="group snap-start shrink-0 w-[78vw] sm:w-[360px]">
                  <div className="aspect-[4/3] overflow-hidden rounded-xl mb-4" style={{ backgroundColor: '#f0f0f0' }}>
                    {ev.cover_url && <img src={ev.cover_url} alt={ev.title} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />}
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.12em] v3-mute">{f.year} · {f.month} {f.day}</span>
                  <h4 className="v3-display-cjk text-[clamp(20px,1.8vw,26px)] mt-1">{ev.title}</h4>
                  <span className="text-[13px] v3-soft">{venueShort(ev.slots[0].location ?? ev.location)}</span>
                </Link>
              );
            })}
          </div>
        </V3RevealSection>
      )}

      {/* PARTNERS — big marquee */}
      <section className="py-20 md:py-28" style={{ borderTop: `1px solid ${LINE}` }}>
        <div className="mx-auto max-w-[1480px] px-6 md:px-10 mb-8">
          <Eyebrow>{t.sections.partners}</Eyebrow>
        </div>
        <PartnerMarquee />
      </section>

      {/* PULL QUOTE */}
      <V3RevealSection className="px-6 md:px-10 py-28 md:py-40" style={{ borderTop: `1px solid ${LINE}` }}>
        <div className="mx-auto max-w-[1100px]">
          <KineticTitle text={t.pull.quote} className={`${disp} text-[clamp(26px,4vw,62px)]`} />
          <p className="mt-10 text-[12px] uppercase tracking-[0.18em] v3-mute font-medium">{t.pull.attr}</p>
        </div>
      </V3RevealSection>
      </div>
    </V3Layout>
  );
}

/* ──────── Hero ────────
 * Sticky, full-screen. The media sits in an inset rounded frame that scales
 * down + rounds further as you scroll; the rest of the page (rounded-top
 * wrapper) slides UP over this pinned hero and reveals it again at the top. */
function Hero({ t, disp, latest, lang }: { t: typeof COPY[keyof typeof COPY]; disp: string; latest?: Activity; ftx: typeof FEAT_TXT[keyof typeof FEAT_TXT]; lang: 'CN' | 'EN' | 'DE' }) {
  const [reduce, setReduce] = useState(false);
  const innerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { setReduce(window.matchMedia('(prefers-reduced-motion: reduce)').matches); }, []);

  useEffect(() => {
    if (reduce) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const p = Math.min(1, Math.max(0, window.scrollY / (window.innerHeight || 1)));
      if (innerRef.current) innerRef.current.style.transform = `translateY(${(p * 36).toFixed(1)}px) scale(1.1)`;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, [reduce]);

  // A strong live photo as the fallback (the gradient tour poster reads poorly here).
  const FALLBACK_LIVE = '/images/events/oys-berlin-2026/photo-JM502484.jpg';
  const card = latest
    ? { href: `/next/events/${latest.id}`, img: latest.cover_url ?? FALLBACK_LIVE, title: latest.title }
    : { href: '/next/events', img: FALLBACK_LIVE, title: lang === 'CN' ? '查看全部活动' : lang === 'DE' ? 'Alle Events' : 'See all events' };
  const latestLabel = lang === 'CN' ? '最近演出' : lang === 'DE' ? 'Aktuell' : 'Latest';

  // Hero scrolls up with the page (not pinned). Media sits in an inset rounded
  // frame so it reads as slightly smaller than the page edge.
  return (
    <section className="relative h-[100svh] flex flex-col justify-end overflow-hidden" style={{ backgroundColor: PAPER }}>
      <div className="absolute overflow-hidden" style={{ inset: 8, borderRadius: '24px 24px 44px 44px', backgroundColor: DARK }}>
        <div ref={innerRef} className="absolute inset-0 will-change-transform">
          {!reduce && (
            <video autoPlay muted loop playsInline preload="metadata" poster="/images/highlights/fazi.jpg" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-65">
              <source src="/band.mp4" type="video/mp4" />
            </video>
          )}
        </div>
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.05) 32%, rgba(0,0,0,0.55) 100%)' }} />
      </div>

      {/* Big dominant headline, bottom-left (wolverine proportion), kept to the
          left ~half so it never covers the latest card on the right. */}
      <div className="relative z-10 mx-auto max-w-[1480px] w-full px-8 md:px-14 pb-14 md:pb-16">
        <div className="md:max-w-[56%]">
          <Eyebrow accent>{t.hero.eyebrow}</Eyebrow>
          <KineticTitle light text={t.hero.lines.join('\n') + '.'} className={`${disp} mt-5 text-[clamp(44px,8vw,140px)]`} />
        </div>
      </div>

      <Link to={card.href} className="absolute right-8 md:right-14 bottom-14 md:bottom-16 z-10 w-[280px] group hidden md:block">
        <div className="aspect-[16/10] overflow-hidden rounded-xl mb-3 shadow-[0_24px_56px_-22px_rgba(0,0,0,0.7)]">
          <img src={card.img} alt={card.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        </div>
        <span className="text-[11px] uppercase tracking-[0.18em] text-white/65">{latestLabel}</span>
        <p className="text-white text-[18px] font-semibold leading-snug flex items-center gap-1.5 mt-0.5">{card.title} <span className="text-white/55 text-[14px]">↗</span></p>
      </Link>
    </section>
  );
}

/* ──────── Program block ──────── */
function ProgramBlock({ to, kicker, title, sub, body, cta }: { to: string; kicker: string; title: string; sub: string; body: string; cta: string }) {
  return (
    <Link to={to} className="group block bg-white p-8 md:p-12 transition-colors hover:bg-[#fafafa]">
      <span className="text-[11px] tracking-[0.18em] uppercase v3-mute font-semibold">{kicker}</span>
      <h4 className="v3-display text-[clamp(30px,3.8vw,60px)] mt-4 flex items-baseline gap-3 flex-wrap">
        {title}<span className="v3-display-cjk text-[0.5em] v3-mute">{sub}</span>
      </h4>
      <p className="mt-6 text-[15px] leading-[1.6] v3-soft max-w-[44ch]">{body}</p>
      <span className="inline-flex items-center gap-2 mt-8 text-[15px] font-semibold v3-ink">
        {cta}<span className="transition-transform group-hover:translate-x-1" style={{ color: ACCENT }}>→</span>
      </span>
    </Link>
  );
}

/* ──────── Big stat ──────── */
function BigStat({ value, label, small }: { value: string; label: string; small?: boolean }) {
  return (
    <V3Reveal className="border-t pt-6" style={{ borderColor: LINE }}>
      <div className={`v3-display ${small ? 'text-[clamp(28px,3.6vw,52px)]' : 'text-[clamp(46px,8vw,116px)]'} leading-[0.85]`}>{value}</div>
      <div className="mt-4 text-[12px] uppercase tracking-[0.14em] v3-mute font-medium">{label}</div>
    </V3Reveal>
  );
}

/* ──────── Partner marquee ──────── */
function PartnerMarquee() {
  const row = PARTNERS.map((p) => p.name);
  const doubled = [...row, ...row];
  return (
    <div className="relative w-full overflow-hidden select-none" aria-hidden>
      <div className="flex w-max animate-marquee whitespace-nowrap">
        {doubled.map((name, i) => (
          <span key={i} className="flex items-center">
            <span className="v3-display text-[clamp(22px,2.8vw,42px)] px-8" style={{ color: INK }}>{name}</span>
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ACCENT }} />
          </span>
        ))}
      </div>
    </div>
  );
}
